import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CategoryIcon } from "@/components/CategoryIcon";
import { StatusBadge } from "@/components/StatusBadge";
import { formatTimeAgo } from "@/lib/utils-app";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { MapPin, Zap, ChevronRight, PlusCircle } from "lucide-react";

export function MyRequests() {
  const { data: requests, isLoading } = useQuery({
    queryKey: ["my-requests"],
    queryFn: async () => (await api.get("/requests/mine")).data,
  });

  return (
    <div className="px-4 sm:px-6 pt-6 sm:pt-10 space-y-4" data-testid="my-requests-page">
      <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Le mie richieste</h1>

      {isLoading ? (
        <div className="space-y-3">{[1, 2].map((i) => <Skeleton key={i} className="h-28 rounded-3xl" />)}</div>
      ) : requests?.length ? (
        <div className="grid gap-3 sm:grid-cols-2" data-testid="my-requests-list">
          {requests.map((r) => (
            <Link key={r.id} to={`/richieste/${r.id}`} data-testid={`my-request-${r.id}`}
              className="block bg-white rounded-3xl border border-slate-100 shadow-sm p-4 space-y-2 hover:border-whoo/40 transition-colors active:scale-[0.99]">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="w-9 h-9 rounded-xl bg-whoo-soft text-whoo flex items-center justify-center shrink-0">
                    <CategoryIcon icon={r.category} className="w-4 h-4" />
                  </span>
                  <p className="font-semibold text-slate-900 text-sm leading-snug">{r.title || r.raw_text?.slice(0, 80)}</p>
                </div>
                <StatusBadge status={r.status} testid={`my-request-status-${r.id}`} />
              </div>
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{r.zone || r.address || "—"}</span>
                <span className="flex items-center gap-2">
                  {r.urgent && <span className="text-whoo font-bold flex items-center gap-1"><Zap className="w-3.5 h-3.5" />Urgente</span>}
                  {formatTimeAgo(r.created_at)}
                </span>
              </div>
              <div className="flex items-center justify-end text-xs font-semibold text-volt">
                Dettagli <ChevronRight className="w-3.5 h-3.5" />
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-8 text-center space-y-4" data-testid="empty-requests">
          <p className="text-slate-600 text-sm">Non hai ancora pubblicato richieste.</p>
          <Link to="/richieste/nuova">
            <Button data-testid="empty-new-request-btn" className="rounded-full bg-whoo hover:bg-whoo-dark text-white font-semibold active:scale-95 transition-transform">
              <PlusCircle className="w-4 h-4 mr-2" /> Crea la prima richiesta
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
}
